/* @flow */
import moment from 'moment';
import * as React from 'react';
import { observable } from 'mobx';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';

import type TypeForm from 'forms/_.extend';
import Loading from 'components/Loading/Loading';
import { getCurrencySymbol } from 'utils/getCurrencySymbol';
import SiteAddress from 'components/SiteAddress/SiteAddress';
import { CAAS_ACTIONS, ADDRESS_OPTS, CONTACT_OPTS } from './constants';
import CaaSAssetColectionDetails from 'utils/CaaSAssetCollectionDetails';
import ElectroSelect from 'forms/components/inputs/ElectroSelect/ElectroSelect';
import { NumberedCarousel } from 'views/ActionDrawer/components/NumberedCarousel/index';
import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroRadioGroup from 'forms/components/inputs/ElectroRadioGroup/ElectroRadioGroup';
import ActionDrawerBlock from 'views/ActionDrawer/components/ActionDrawerBlock/ActionDrawerBlock';
import ActionDrawerTable from 'views/ActionDrawer/components/ActionDrawerTable/ActionDrawerTable';
import ActionDrawerFormButtons from 'views/ActionDrawer/components/ActionDrawerFormButtons/ActionDrawerFormButtons';
import ElectroDatePickerWithoutIcon from 'forms/components/inputs/ElectroDatePickerWithoutIcons/ElectroDatePickerWithoutIcons';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {
  t: TranslatorProps,
  asset: Object,
  assetStore: Object,
  form: TypeForm,
};

const buttonText = {
  [CAAS_ACTIONS.caas_schedule.name]: 'Create Work Order',
  [CAAS_ACTIONS.caas_updates.name]: 'Update Work Order',
  [CAAS_ACTIONS.caas_completion.name]: 'Close Work Order',
};

@translate()
@inject('assetStore')
@observer
export default class CaaSForm extends React.Component<*> {
  handleCollectionAddressOnChange = (value: string) => {
    const caasAssetCollectionDetails = new CaaSAssetColectionDetails(this.props.assetStore);
    const addressField = this.props.form.$('caasDetails.collection_address');

    switch (value) {
      case ADDRESS_OPTS.currentAddress:
        addressField.set(caasAssetCollectionDetails.getLocationAddress());
        break;
      case ADDRESS_OPTS.newAddress:
        addressField.set(caasAssetCollectionDetails.getCustomAddress());
        break;
    }
  };

  handleContactDetailsOnChange = (value: string) => {
    const caasAssetCollectionDetails = new CaaSAssetColectionDetails(this.props.assetStore);
    const assetContactDetails = caasAssetCollectionDetails.getAssetContactDetails();
    const customContactDetails = caasAssetCollectionDetails.getCustomContactDetails();

    const contactNameField = this.props.form.$('caasDetails.contact_name');
    const contactPhoneField = this.props.form.$('caasDetails.contact_phone');

    switch (value) {
      case CONTACT_OPTS.currentContact:
        contactNameField.set(assetContactDetails.name);
        contactPhoneField.set(assetContactDetails.phone);
        break;
      case CONTACT_OPTS.newContact:
        contactNameField.set(customContactDetails.name);
        contactPhoneField.set(customContactDetails.phone);
        break;
    }
  };

  caasScheduleFormValidation = form => {
    // Mandatory fields if 'New Address' option is selected
    if (
      form.$('caasDetails.collectionAddress').value === ADDRESS_OPTS.newAddress &&
      !form.$('caasDetails.collection_address').value
    ) {
      return true;
    }

    // Mandatory fields if 'New Contact' option is selected
    if (
      form.$('caasDetails.contactDetails').value === CONTACT_OPTS.newContact &&
      (!form.$('caasDetails.contact_name').value || !form.$('caasDetails.contact_phone').value)
    ) {
      return true;
    }

    return false;
  };

  caasScheduleMultipleFormValidation = form => {
    // Mandatory fields
    return (
      !form.$('caasDetails.collection_address').value ||
      !form.$('caasDetails.contact_name').value ||
      !form.$('caasDetails.contact_phone').value
    );
  };

  caasUpdatesFormValidation = form => {
    return (
      form.$('caasDetails.workOrderNumber').value.length < 8 ||
      form.$('caasDetails.workOrderNumber').value.length > 32
    );
  };

  checkActionButtonStatus = (currentAction, form) => {
    const { renderMultiple } = this.props;

    if (currentAction === CAAS_ACTIONS.caas_schedule.name) {
      return renderMultiple
        ? this.caasScheduleMultipleFormValidation(form)
        : this.caasScheduleFormValidation(form);
    }

    if (currentAction === CAAS_ACTIONS.caas_updates.name) {
      return this.caasUpdatesFormValidation(form);
    }

    return false;
  };

  renderCaaSScheduleForm = form => {
    if (this.props.renderMultiple) {
      return (
        <>
          <ActionDrawerBlock divider={true} gutters={true} heading={'Collection Details'}>
            <ElectroTextField
              field={form.$('caasDetails.collection_address')}
              disabled={false}
              required={true}
            />
          </ActionDrawerBlock>
          <ActionDrawerBlock divider={true} gutters={true} heading={'Collection Date'}>
            <ElectroDatePickerWithoutIcon
              field={form.$('caasDetails.collectionDate')}
              minDate={
                new Date(
                  moment()
                    .add(1, 'd')
                    .format('YYYY-MM-DD')
                )
              }
            />
          </ActionDrawerBlock>
          <ActionDrawerBlock divider={true} gutters={true} heading={'Contact Details'}>
            <ElectroTextField
              field={form.$('caasDetails.contact_name')}
              disabled={false}
              required={true}
            />
            <ElectroTextField
              field={form.$('caasDetails.contact_phone')}
              disabled={false}
              required={true}
            />
          </ActionDrawerBlock>
        </>
      );
    }

    const useCustomContact = form.$('caasDetails.contactDetails').value === CONTACT_OPTS.newContact;
    const useCustomAddress =
      form.$('caasDetails.collectionAddress').value === ADDRESS_OPTS.newAddress;

    return (
      <>
        <ActionDrawerBlock divider={true} gutters={true} heading={'Collection Details'}>
          <ElectroRadioGroup
            field={form.$('caasDetails.collectionAddress')}
            onChange={this.handleCollectionAddressOnChange}
          />
          <ElectroTextField
            field={form.$('caasDetails.collection_address')}
            disabled={!useCustomAddress}
          />
        </ActionDrawerBlock>
        <ActionDrawerBlock divider={true} gutters={true} heading={'Collection Date'}>
          <ElectroDatePickerWithoutIcon
            field={form.$('caasDetails.collectionDate')}
            minDate={
              new Date(
                moment()
                  .add(1, 'd')
                  .format('YYYY-MM-DD')
              )
            }
          />
        </ActionDrawerBlock>
        <ActionDrawerBlock divider={true} gutters={true} heading={'Contact Details'}>
          <ElectroRadioGroup
            field={form.$('caasDetails.contactDetails')}
            onChange={this.handleContactDetailsOnChange}
          />
          <ElectroTextField
            field={form.$('caasDetails.contact_name')}
            disabled={!useCustomContact}
          />
          <ElectroTextField
            field={form.$('caasDetails.contact_phone')}
            disabled={!useCustomContact}
          />
        </ActionDrawerBlock>
      </>
    );
  };

  renderCaaSUpdatesForm = form => {
    return (
      <ActionDrawerBlock divider={true} gutters={true} heading={'Work Order Number'}>
        <ElectroTextField field={form.$('caasDetails.workOrderNumber')} required={true} />
      </ActionDrawerBlock>
    );
  };

  renderCaaSCompletionForm = form => {
    return (
      <ActionDrawerBlock divider={true} gutters={true} heading={'CaaS Close Date'}>
        <ElectroDatePickerWithoutIcon
          field={form.$('caasDetails.completionDate')}
          minDate={
            new Date(
              moment()
                .subtract(30, 'd')
                .format('YYYY-MM-DD')
            )
          }
          maxDate={
            new Date(
              moment()
                .add(1, 'd')
                .format('YYYY-MM-DD')
            )
          }
        />
      </ActionDrawerBlock>
    );
  };

  renderCarousel() {
    const { t, form, assetStore } = this.props;
    const currentAction = form.$('caasDetails.caas_actions').value;

    return (
      <ActionDrawerBlock
        divider={false}
        gutters={true}
        heading={t('general:assets.labels.details')}
      >
        <NumberedCarousel>
          {assetStore.assets.map(assetItem => {
            let assetInfo: Array<*> = [
              ['Trading Account', assetItem.account.name],
              ['Asset Number', assetItem.asset_number],
              ['Manufacturer', assetItem.manufacturer],
              ['Model', assetItem.model],
              ['OEM Serial Number', assetItem.serial_number],
              ['Rental Backfill', assetItem.calibration_service?.is_rental_backfill ? 'Yes' : 'No'],
              ['Calibration Type', assetItem.calibration_service?.type],
            ];

            if (currentAction !== CAAS_ACTIONS.caas_schedule.name) {
              const pricePrefix = getCurrencySymbol(assetItem.calibration_service?.currency);
              assetInfo.push(
                ['Calibration Vendor', assetItem.calibration_service?.vendor],
                ['Calibration Currency', assetItem.calibration_service?.currency],
                ['Calibration Price', `${pricePrefix} ${assetItem.calibration_service?.price}`]
              );
            }

            return (
              <React.Fragment key={assetItem.id}>
                <ActionDrawerTable data={assetInfo} />
                <div className={styles['clearfix']} />
                <div className={styles['info-block']}>
                  <h3>{t('general:assets.labels.current_location')}</h3>
                  {assetItem.location ? (
                    <SiteAddress {...assetItem.location.site.address} />
                  ) : (
                    <ul>
                      <li>{t('general:search_results.no_location_found')}</li>
                    </ul>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </NumberedCarousel>
      </ActionDrawerBlock>
    );
  }

  renderDetails() {
    const { t, form, assetStore }: TypeProps = this.props;

    const assetDetails: Object = assetStore;
    const currentAction = form.$('caasDetails.caas_actions').value;

    let assetInfo: Array<*> = [
      ['Trading Account', assetDetails.asset.account.name],
      ['Asset Number', assetDetails.asset.asset_number],
      ['Manufacturer', assetDetails.asset.manufacturer],
      ['Model', assetDetails.asset.model],
      ['OEM Serial Number', assetDetails.asset.serial_number],
      [
        'Rental Backfill',
        assetDetails.asset.calibration_service?.is_rental_backfill ? 'Yes' : 'No',
      ],
      ['Calibration Type', assetDetails.asset.calibration_service?.type],
    ];

    if (currentAction !== CAAS_ACTIONS.caas_schedule.name) {
      const pricePrefix = getCurrencySymbol(assetDetails.asset?.calibration_service?.currency);
      assetInfo.push(
        ['Calibration Vendor', assetDetails.asset?.calibration_service?.vendor],
        ['Calibration Currency', assetDetails.asset?.calibration_service?.currency],
        ['Calibration Price', `${pricePrefix} ${assetDetails.asset?.calibration_service?.price}`]
      );
    }

    return (
      <>
        <ActionDrawerBlock
          divider={true}
          gutters={true}
          heading={t('general:assets.labels.details')}
        >
          <ActionDrawerTable data={assetInfo} />
          <div className={styles['clearfix']} />
          <div className={styles['info-block']}>
            <h3>{t('general:assets.labels.current_location')}</h3>
            {assetDetails.asset?.location ? (
              <SiteAddress {...assetDetails.asset.location.site.address} />
            ) : (
              <ul>
                <li>{t('general:search_results.no_location_found')}</li>
              </ul>
            )}
          </div>
        </ActionDrawerBlock>
      </>
    );
  }

  render() {
    const { t, form, assetStore, renderMultiple }: TypeProps = this.props;
    const currentAction = form.$('caasDetails.caas_actions').value;
    const isButtonDisabled = this.checkActionButtonStatus(currentAction, form);
    const itemsAvailable = renderMultiple ? !assetStore.assets : !assetStore.asset;

    if (itemsAvailable) {
      return <Loading />;
    }

    return (
      <React.Fragment>
        <div className={styles['action-drawer-content']}>
          {renderMultiple ? this.renderCarousel() : this.renderDetails()}
          <ActionDrawerBlock divider={true} gutters={true} heading="CaaS Actions">
            <ElectroSelect
              field={form.$('caasDetails.caas_actions')}
              searchable={false}
              simpleValue={true}
            />
          </ActionDrawerBlock>
          {currentAction === CAAS_ACTIONS.caas_schedule.name && this.renderCaaSScheduleForm(form)}
          {currentAction === CAAS_ACTIONS.caas_updates.name && this.renderCaaSUpdatesForm(form)}
          {currentAction === CAAS_ACTIONS.caas_completion.name &&
            this.renderCaaSCompletionForm(form)}
        </div>
        <ActionDrawerFormButtons
          form={form}
          submitTxt={buttonText[currentAction]}
          disabled={isButtonDisabled}
        />
      </React.Fragment>
    );
  }
}
